import { supabase } from '../lib/supabase';
import Papa from 'papaparse';

const REQUIRED_FIELDS = ['institute_name', 'email', 'city'];

const VALID_CATEGORIES = [
  'Engineering',
  'Management',
  'Law',
  'Medical',
  'Arts & Science',
  'Architecture',
  'Pharmacy',
  'Agriculture',
  'Design',
  'Hotel Management',
  'Education',
  'Nursing',
];

export const bulkInstituteService = {
  /**
   * Generate a strong password for institute
   * Format: StudyExpo@[InstituteName][Year]
   */
  generatePassword(instituteName) {
    const year = new Date()?.getFullYear();
    // Take first word of institute name, remove special chars, capitalize
    const nameSlug = (instituteName || 'Institute')?.split(' ')?.[0]?.replace(/[^a-zA-Z0-9]/g, '')?.substring(0, 12);
    return `StudyExpo@${nameSlug}${year}`;
  },

  /**
   * Download Excel template for institute bulk upload
   */
  downloadTemplate() {
    const headers = ['institute_name', 'email', 'city', 'state', 'phone', 'website', 'categories'];
    const guideRow = [
      '--- GUIDE ROW (DELETE BEFORE UPLOADING) ---',
      '',
      '',
      '',
      '',
      '',
      'VALID CATEGORIES (comma-separated): Engineering | Management | Law | Medical | Arts & Science | Architecture | Pharmacy | Agriculture | Design | Hotel Management | Education | Nursing',
    ];
    const sampleRows = [
      ['VIT Vellore', 'admissions@vitvellore.ac.in', 'Vellore', 'Tamil Nadu', '+919876543210', 'www.vit.ac.in', 'Engineering,Management,Law,Architecture'],
      ['IIT Bombay', 'admissions@iitb.ac.in', 'Mumbai', 'Maharashtra', '+912222578000', 'www.iitb.ac.in', 'Engineering'],
      ['AIIMS Delhi', 'admissions@aiims.ac.in', 'New Delhi', 'Delhi', '+911126588500', 'www.aiims.edu', 'Medical,Pharmacy,Nursing'],
      ['NLU Delhi', 'admissions@nludelhi.ac.in', 'New Delhi', 'Delhi', '+911147074000', 'www.nludelhi.ac.in', 'Law'],
    ];

    const csvContent = [headers, guideRow, ...sampleRows]?.map(row => row?.map(cell => `"${cell}"`)?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'institute_bulk_upload_template.csv';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Parse CSV/Excel file
   */
  parseFile(file) {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header?.trim()?.toLowerCase()?.replace(/\s+/g, '_'),
        complete: (results) => resolve(results),
        error: (error) => reject(new Error(`File parsing failed: ${error?.message}`)),
      });
    });
  },

  /**
   * Validate parsed data
   */
  validateData(data) {
    const errors = [];
    const validRows = [];

    if (!data?.length) {
      return { valid: false, errors: ['File is empty'], validRows: [], totalRows: 0, validCount: 0, errorCount: 0 };
    }

    const headers = Object.keys(data?.[0] || {});
    const missingHeaders = REQUIRED_FIELDS?.filter(f => !headers?.includes(f));
    if (missingHeaders?.length > 0) {
      return {
        valid: false,
        errors: [`Missing required columns: ${missingHeaders?.join(', ')}`],
        validRows: [],
        totalRows: data?.length,
        validCount: 0,
        errorCount: data?.length,
      };
    }

    const seenEmails = new Set();

    data?.forEach((row, index) => {
      const rowNum = index + 2;
      const rowErrors = [];

      if (!row?.institute_name?.trim()) rowErrors?.push(`Row ${rowNum}: institute_name is required`);
      if (!row?.email?.trim()) {
        rowErrors?.push(`Row ${rowNum}: email is required`);
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(row?.email?.trim())) {
        rowErrors?.push(`Row ${rowNum}: invalid email format`);
      } else if (seenEmails?.has(row?.email?.trim()?.toLowerCase())) {
        rowErrors?.push(`Row ${rowNum}: duplicate email ${row?.email}`);
      } else {
        seenEmails?.add(row?.email?.trim()?.toLowerCase());
      }
      if (!row?.city?.trim()) rowErrors?.push(`Row ${rowNum}: city is required`);

      // Validate categories if provided
      if (row?.categories?.trim()) {
        const cats = row?.categories?.split(',')?.map(c => c?.trim())?.filter(Boolean);
        const invalidCats = cats?.filter(c => !VALID_CATEGORIES?.includes(c));
        if (invalidCats?.length > 0) {
          rowErrors?.push(`Row ${rowNum}: invalid categories: ${invalidCats?.join(', ')}. Valid: ${VALID_CATEGORIES?.join(', ')}`);
        }
      }

      if (rowErrors?.length > 0) {
        errors?.push(...rowErrors);
      } else {
        validRows?.push({ ...row, rowNumber: rowNum });
      }
    });

    return {
      valid: errors?.length === 0,
      errors,
      validRows,
      totalRows: data?.length,
      validCount: validRows?.length,
      errorCount: errors?.length,
    };
  },

  /**
   * Parse categories string into array
   */
  parseCategories(categoriesStr) {
    if (!categoriesStr?.trim()) return [];
    return categoriesStr?.split(',')?.map(c => c?.trim())?.filter(c => VALID_CATEGORIES?.includes(c));
  },

  /**
   * Create a single institute account
   * Uses the create-institute-user Edge Function which calls supabase.auth.admin.createUser()
   * This bypasses Supabase email rate limits entirely — no confirmation email is sent.
   */
  async createInstituteAccount(rowData) {
    const email = rowData?.email?.trim()?.toLowerCase();
    const instituteName = rowData?.institute_name?.trim();
    const password = this.generatePassword(instituteName);
    const categories = this.parseCategories(rowData?.categories || '');

    // Call the Edge Function which uses the service role key + Admin API
    // admin.createUser() with email_confirm:true skips confirmation emails → no rate limits
    const { data: fnData, error: fnError } = await supabase?.functions?.invoke('create-institute-user', {
      body: {
        email,
        password,
        instituteName,
        city: rowData?.city?.trim() || null,
        state: rowData?.state?.trim() || null,
        phone: rowData?.phone?.trim() || null,
        website: rowData?.website?.trim() || null,
        categories,
      },
    });

    if (fnError) {
      // fnError.message is often just "Edge Function returned a non-2xx status code"
      // The real error detail is in fnError.context (the response body)
      let msg = fnError?.message || String(fnError);

      // Try to extract the actual error from the response body
      try {
        const contextBody = fnError?.context;
        if (contextBody) {
          let parsed = contextBody;
          if (typeof contextBody === 'string') {
            parsed = JSON.parse(contextBody);
          } else if (contextBody instanceof Response) {
            const text = await contextBody?.text();
            parsed = JSON.parse(text);
          } else if (contextBody?.json) {
            parsed = await contextBody?.json();
          }
          if (parsed?.error) {
            msg = parsed?.error;
          } else if (parsed?.message) {
            msg = parsed?.message;
          }
        }
      } catch (_) {
        // fallback to original message
      }

      if (msg?.includes('rate_limit') || msg?.includes('rate limit') || msg?.includes('Rate limit') || msg?.includes('Too Many Requests') || fnError?.context?.status === 429) {
        throw Object.assign(new Error(`rate_limit_exceeded: ${msg}`), { status: 429 });
      }
      if (msg?.includes('already exists') || msg?.includes('already registered')) {
        throw new Error(`Email ${email} already exists`);
      }
      throw new Error(msg);
    }

    if (!fnData?.success) {
      let msg = fnData?.error || 'Unknown error from create-institute-user function';
      if (msg?.includes('rate_limit') || msg?.includes('rate limit') || msg?.includes('Rate limit') || msg?.includes('Too Many Requests')) {
        throw Object.assign(new Error(`rate_limit_exceeded: ${msg}`), { status: 429 });
      }
      if (msg?.includes('already exists') || msg?.includes('already registered')) {
        throw new Error(`Email ${email} already exists`);
      }
      throw new Error(msg);
    }

    return { email, password, instituteName, userId: fnData?.userId, categories };
  },

  /**
   * Send welcome email with credentials via existing send-email edge function
   * DISABLED: No emails are sent to institutes at any stage (bulk upload, pending, or approval).
   * Super admin will personally email institutes from their own email after approval.
   */
  async sendWelcomeEmail(email, instituteName, password) {
    // Email notifications to institutes are disabled.
    // Credentials are available via CSV download for the super admin to share manually.
    console.log('[BulkUpload] Welcome email disabled for institute:', instituteName, '- credentials available in CSV download');
    return;
  },

  /**
   * Wait for a given number of milliseconds
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  },

  /**
   * Bulk create institute accounts with progress callback
   * Includes smart retry: rate-limited institutes are re-queued and retried with exponential backoff
   */
  async bulkCreateInstitutes(validRows, onProgress) {
    const results = { successful: [], failed: [] };
    const total = validRows?.length;

    // Build a queue: each entry tracks the row + retry metadata
    const queue = validRows?.map(row => ({ row, attempt: 0 }));
    let processed = 0;

    while (queue?.length > 0) {
      const item = queue?.shift();
      const { row, attempt } = item;

      // Delay between every call to stay under Admin API rate limits
      // Base delay: 3s between normal requests; exponential backoff for retries: 15s, 30s, 60s
      if (processed > 0 || attempt > 0) {
        const waitMs = attempt > 0 ? Math.min(15000 * Math.pow(2, attempt - 1), 60000) : 3000;
        if (attempt > 0) {
          // Notify progress that we're waiting on a retry
          if (onProgress) {
            onProgress({
              current: processed,
              total,
              percentage: Math.round((processed / total) * 100),
              retrying: true,
              retryInfo: `Rate limit hit — retrying ${row?.institute_name?.trim()} in ${waitMs / 1000}s (attempt ${attempt + 1}/4)`,
            });
          }
        }
        await this.delay(waitMs);
      }

      try {
        const result = await this.createInstituteAccount(row);
        results?.successful?.push({
          rowNumber: row?.rowNumber,
          instituteName: result?.instituteName,
          email: result?.email,
          password: result?.password,
          city: row?.city?.trim() || '',
          state: row?.state?.trim() || '',
          status: 'success',
        });
        processed++;
      } catch (error) {
        const errMsg = error?.message || '';
        const isRateLimit =
          errMsg?.includes('rate_limit') ||
          errMsg?.includes('rate limit') ||
          errMsg?.includes('Rate limit') ||
          errMsg?.includes('Request rate limit') ||
          errMsg?.includes('over_email_send_rate_limit') ||
          errMsg?.includes('email rate limit') ||
          errMsg?.includes('Too Many Requests') ||
          error?.status === 429 ||
          error?.context?.status === 429;

        if (isRateLimit && attempt < 3) {
          // Re-queue with incremented attempt counter (do NOT count as processed)
          queue?.unshift({ row, attempt: attempt + 1 });
        } else {
          // Permanent failure (non-rate-limit error, or exhausted retries)
          results?.failed?.push({
            rowNumber: row?.rowNumber,
            instituteName: row?.institute_name?.trim() || '',
            email: row?.email?.trim() || '',
            error: isRateLimit
              ? `Rate limit exceeded after ${attempt + 1} attempts — try uploading this institute separately later`
              : errMsg || 'Unknown error',
            status: 'failed',
          });
          processed++;
        }
      }

      if (onProgress) {
        onProgress({
          current: processed,
          total,
          percentage: Math.round((processed / total) * 100),
          retrying: false,
        });
      }
    }

    return results;
  },

  /**
   * Export credentials to CSV for download
   */
  exportCredentialsToCSV(results) {
    const rows = results?.successful || [];
    if (!rows?.length) return;

    const headers = ['Institute Name', 'Email', 'Password', 'City', 'State', 'Categories', 'Status'];
    const csvRows = rows?.map(r => [
      `"${r?.instituteName}"`,
      `"${r?.email}"`,
      `"${r?.password}"`,
      `"${r?.city}"`,
      `"${r?.state}"`,
      `"${(r?.categories || [])?.join(', ')}"`,
      `"${r?.status}"`,
    ]?.join(','));

    // Also add failed rows
    const failedRows = (results?.failed || [])?.map(r => [
      `"${r?.instituteName}"`,
      `"${r?.email}"`,
      `"N/A"`,
      `""`,
      `""`,
      `""`,
      `"FAILED: ${r?.error}"`,
    ]?.join(','));

    const csvContent = [headers?.join(','), ...csvRows, ...failedRows]?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `institute_credentials_${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  },
};
